import React, { useState, useEffect, useMemo, useCallback } from "react";
import makeStyles from "@mui/styles/makeStyles";
import EditableTable from "../../../../Controllers/EditableTable";

import { Box, Grid, Paper, Typography } from "@mui/material";
import service from "./../../helpers/GifService";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  applicantSelector,
  canEditSelector,
  isLeaderSelector,
  reportingAsSelector,
} from "../../../../store/applicantsSlice";
import { useSelector } from "react-redux";
import ApplicantSelector from "../../Components/applicantSelector";
import { useLoading } from "../../../../providers/loadingProvider";
import { useNotify } from "../../../../providers/NotificationProvider";
import {
  colleaguesSelector,
  proposalSelector,
} from "../../../../store/proposalSlice";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
    overflowX: "auto",
  },
  table: {
    minWidth: 650,
  },
  infoIcon: {
    marginLeft: 5,
  },

  header: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
  },
}));

export default function Academic() {
  const classes = useStyles();
  const notify = useNotify();
  const as = useSelector(reportingAsSelector);
  const isLeader = useSelector(isLeaderSelector);
  const proposal = useSelector(proposalSelector);
  const colleagues = useSelector(colleaguesSelector);
  const canEdit = useSelector(canEditSelector);
  const applicant = useSelector(applicantSelector);
  const [proposalApplicants, setProposalApplicants] = useState([]);
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [academicBackgrounds, setAcademicBackgrounds] = useState([]);
  const loading = useLoading();

  const validateRow = (row) => {
    let errorList = [];
    if (row.from === null || row.from === undefined || row.from === "")
      errorList.push("From");
    if (row.to === null || row.to === undefined || row.to === "")
      errorList.push("To");
    if (
      row.institutionName === null ||
      row.institutionName === undefined ||
      row.institutionName === ""
    )
      errorList.push("Institution Name");
    if (
      row.areaofspecialization === null ||
      row.areaofspecialization === undefined ||
      row.areaofspecialization === ""
    )
      errorList.push("Area of Specialication");
    if (row.degree === null || row.degree === undefined || row.degree === "")
      errorList.push("Degree");

    if (errorList.length === 0) return true;

    notify.show(`Missing Fields:  ${errorList.join(", ")}`, {
      severity: "error",
    });

    return false;
  };

  const setRow = (row, index) => {
    const loadingId = loading.addLoading("saving academic background");
    var newRows = academicBackgrounds;
    newRows[index] = row;
    service.updateApplicantAcademicBackground({
      academicBackground: row,
    });
    getAllAcademic();
    loading.removeLoading(loadingId);
  };

  const addRow = async (row) => {
    row.name = "AcademicBG";
    const loadingId = loading.addLoading("adding academic background");
    const data = { proposalId: proposal.id, ...row };
    await service.createApplicantAcademicBackground({
      academicBackground: data,
    });
    getAllAcademic();
    loading.removeLoading(loadingId);
  };

  const deleteRow = async (academicBackgroundId) => {
    const loadingId = loading.addLoading("removing academic background");
    await service.DeactivateAcademicBackground({ academicBackgroundId });
    getAllAcademic();
    loading.removeLoading(loadingId);
  };

  const fillInstitutionsOptions = useCallback(async () => {
    const loadingId = loading.addLoading("loading Institutions");
    var institutions = await service.getAllInstitutions(false);
    setInstitutionOptions(
      institutions.map((i) => ({ ...i, id: i.name, label: i.name }))
    );
    loading.removeLoading(loadingId);
  }, [loading]);

  const getAllAcademic = useCallback(async () => {
    const loadingId = loading.addLoading("loading academic backgrounds");
    let academic = await service.GetApplicantAcademicBackground();
    academic.sort((a,b) => new Date(a.from) - new Date(b.from));
    setAcademicBackgrounds(academic);
    loading.removeLoading(loadingId);
  }, [loading]);

  const getProposalApplicants = useCallback(async () => {
    if (!colleagues) return [];
    return colleagues.map((c) => ({
      id: c.id,
      name: c.name,
    }));
  }, [colleagues]);

  useEffect(() => {
    const getData = async () => {
      setAcademicBackgrounds([]);
      fillInstitutionsOptions();
      const applicants = await getProposalApplicants();
      setProposalApplicants(applicants.map((a) => ({ ...a, label: a.name })));
    };
    getData();
  }, [applicant, fillInstitutionsOptions, getProposalApplicants, loading]);

  useEffect(() => {
    getAllAcademic();
  }, [as, getAllAcademic, loading]);

  const tableConfig = useMemo(() => {
    let leaderColumns = [];
    if (isLeader) {
      leaderColumns = [
        {
          id: "applicantId",
          label: "Name",
          field: "applicantId",
          type: "options",
          options: proposalApplicants,
          width: 3,
          skipValidation: true,
          value: applicant.id,
        },
      ];
    }
    return {
      idField: "applicantacademicbackgroundId",
      disableEdit: !canEdit,
      columns: [
        ...leaderColumns,
        {
          id: "from",
          label: "From",
          field: "from",
          type: "date",
        },
        {
          id: "to",
          label: "To",
          field: "to",
          type: "date",
        },
        {
          id: "institutionName",
          label: "Institution Name",
          field: "institutionName",
          type: "options",
          options: institutionOptions,
          width: 3,
        },
        {
          id: "areaofspecialization",
          label: "Area of Specialization",
          field: "areaofspecialization",
          width: 2,
        },
        {
          id: "degree",
          label: "Degree",
          field: "degree",
        },
      ],
    };
  }, [canEdit, proposalApplicants, isLeader, applicant.id, institutionOptions]);

  return (
    <>
      <Grid container>
        <Box className={classes.header}>
          <Typography variant="subtitle2">Academic Background</Typography>
          <InfoOutlinedIcon className={classes.infoIcon} color="primary" />
          <ApplicantSelector />
        </Box>
      </Grid>
      <Paper className={classes.root} elevation={0}>
        <EditableTable
          tableConfig={tableConfig}
          tableData={academicBackgrounds}
          rowValidate={validateRow}
          rowCreated={addRow}
          rowUpdated={setRow}
          rowDeleted={deleteRow}
        />
      </Paper>
    </>
  );
}
